import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { X, PackageX, Check, RotateCcw, Loader2 } from 'lucide-react';
import { staffApi } from '../../services/api';

const SCOPES = [
  { v: 'all', label: 'Tutti i canali' },
  { v: 'takeaway', label: 'Solo ritiro' },
  { v: 'delivery', label: 'Solo delivery' },
];
const DURATIONS = [
  { v: '60', label: '1 ora' },
  { v: 'eod', label: 'Fine giornata' },
  { v: 'date', label: 'Fino a data…' },
  { v: 'indef', label: 'A tempo indeterminato' },
];

function endOfDayIso() {
  const d = new Date();
  d.setHours(23, 59, 0, 0);
  return d.toISOString();
}

export default function EightySixModal({ onClose }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editFor, setEditFor] = useState(null);
  const [scope, setScope] = useState('all');
  const [duration, setDuration] = useState('eod');
  const [dateVal, setDateVal] = useState('');

  const load = useCallback(async () => {
    try { setItems((await staffApi.get('/kitchen/menu-availability')).data.items || []); }
    catch { toast.error('Errore caricamento prodotti'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const isUnavailable = (it) => !it.available_takeaway || !it.available_delivery;

  const markUnavailable = async (it) => {
    const body = { item_id: it.id, available: false, scope };
    if (duration === '60') body.minutes = 60;
    else if (duration === 'eod') body.until = endOfDayIso();
    else if (duration === 'date') {
      if (!dateVal) { toast.error('Scegli una data'); return; }
      body.until = new Date(dateVal).toISOString();
    } else body.indefinite = true;
    try {
      await staffApi.post('/kitchen/item-availability', body);
      toast.success(`${it.name} segnato non disponibile`);
      setEditFor(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };

  const restore = async (it) => {
    try {
      await staffApi.post('/kitchen/item-availability', { item_id: it.id, available: true });
      toast.success(`${it.name} di nuovo disponibile`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };

  const reactivateAll = async () => {
    try {
      const r = (await staffApi.post('/kitchen/reactivate-all', {})).data;
      toast.success(`Riattivati ${r.reactivated} prodotti`);
      load();
    } catch { toast.error('Errore'); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-end md:items-center justify-center p-0 md:p-4" data-testid="kds-86-modal">
      <div className="bg-[#18181E] text-white w-full md:max-w-lg rounded-t-3xl md:rounded-2xl max-h-[85vh] flex flex-col border border-white/10">
        <div className="flex items-center justify-between p-4 border-b border-white/5">
          <h2 className="font-extrabold text-lg flex items-center gap-2"><PackageX size={18} className="text-orange-400" /> Prodotti non disponibili</h2>
          <div className="flex items-center gap-2">
            <button onClick={reactivateAll} data-testid="kds-86-reactivate-all" className="text-xs font-bold px-3 py-1.5 rounded-full bg-green-500/15 text-green-300 flex items-center gap-1"><RotateCcw size={13} /> Riattiva tutto</button>
            <button onClick={onClose} data-testid="kds-86-close" className="w-8 h-8 flex items-center justify-center rounded-full bg-white/5"><X size={16} /></button>
          </div>
        </div>

        <div className="overflow-y-auto p-3 space-y-2">
          {loading && <div className="flex justify-center py-8"><Loader2 className="animate-spin text-orange-400" /></div>}
          {!loading && items.map(it => {
            const unavail = isUnavailable(it);
            return (
              <div key={it.id} data-testid={`kds-86-item-${it.id}`} className="bg-[#1C1C22] rounded-xl p-3 border border-white/5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-sm">{it.name}</p>
                    <div className="flex gap-1.5 mt-1">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${it.available_takeaway ? 'bg-green-500/15 text-green-300' : 'bg-red-500/15 text-red-300'}`}>Ritiro {it.available_takeaway ? '✓' : '✕'}</span>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${it.available_delivery ? 'bg-green-500/15 text-green-300' : 'bg-red-500/15 text-red-300'}`}>Delivery {it.available_delivery ? '✓' : '✕'}</span>
                    </div>
                  </div>
                  {unavail ? (
                    <button onClick={() => restore(it)} data-testid={`kds-86-restore-${it.id}`} className="text-xs font-bold px-3 py-1.5 rounded-full bg-green-500 text-black flex items-center gap-1"><Check size={13} /> Riattiva</button>
                  ) : (
                    <button onClick={() => setEditFor(editFor === it.id ? null : it.id)} data-testid={`kds-86-mark-${it.id}`} className="text-xs font-bold px-3 py-1.5 rounded-full bg-red-500/20 text-red-300">Segna 86</button>
                  )}
                </div>
                {editFor === it.id && (
                  <div className="mt-3 pt-3 border-t border-white/5 space-y-2" data-testid={`kds-86-form-${it.id}`}>
                    <div className="grid grid-cols-2 gap-2">
                      <select value={scope} onChange={e => setScope(e.target.value)} data-testid="kds-86-scope" className="bg-[#26262E] rounded-lg px-2 py-2 text-sm border border-white/10">
                        {SCOPES.map(s => <option key={s.v} value={s.v}>{s.label}</option>)}
                      </select>
                      <select value={duration} onChange={e => setDuration(e.target.value)} data-testid="kds-86-duration" className="bg-[#26262E] rounded-lg px-2 py-2 text-sm border border-white/10">
                        {DURATIONS.map(d => <option key={d.v} value={d.v}>{d.label}</option>)}
                      </select>
                    </div>
                    {duration === 'date' && (
                      <input type="datetime-local" value={dateVal} onChange={e => setDateVal(e.target.value)} data-testid="kds-86-date" className="w-full bg-[#26262E] rounded-lg px-2 py-2 text-sm border border-white/10" />
                    )}
                    <button onClick={() => markUnavailable(it)} data-testid={`kds-86-confirm-${it.id}`} className="w-full rounded-lg py-2 text-sm font-bold bg-red-500 hover:bg-red-600 text-white">Conferma non disponibile</button>
                  </div>
                )}
              </div>
            );
          })}
          {!loading && items.length === 0 && <p className="text-center text-white/30 py-8 text-sm">Nessun prodotto</p>}
        </div>
      </div>
    </div>
  );
}
